import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/** A single selectable booking description ("Elementen boekingsomschrijving"). */
export interface BookingDescriptionItem {
  /** Stored value. 1-8 are the display criteria; -1/-2 are Persoonscode/Persoonsnaam. */
  number: number;
  /** Displayed text. */
  description: string;
}

/** The full selectable list in both languages. */
export interface BookingDescriptionLists {
  nl: BookingDescriptionItem[];
  en: BookingDescriptionItem[];
}

/** A display criterion currently in use for a general ledger account. */
export interface DisplayCriterion {
  /** Id used for update/delete. Faked (but valid) for the Persoonscode/-naam entries. */
  generalLedgerDisplayCriterionId: string;
  generalLedgerAccountId: string;
  number: number;
  descriptionNL: string;
  descriptionEN: string;
}

interface BookingDescriptionApiItem {
  bookingDescription: string;
  number: number;
}

interface BookingDescriptionListApiModel {
  nl: BookingDescriptionApiItem[] | null;
  en: BookingDescriptionApiItem[] | null;
}

interface DisplayCriterionApiModel {
  generalLedgerDisplayCriterion_ID: string;
  generalLedgerAccount_ID: string;
  number: number;
  bookingDescriptionNL?: string | null;
  bookingDescriptionEN?: string | null;
}

interface DisplayCriterionApiPayload {
  generalLedgerDisplayCriterion_ID: string;
  generalLedgerAccount_ID: string;
  number: number;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * CRUD access to a general ledger account's display criteria
 * ("Elementen boekingsomschrijving"), backed by
 * /kbj-frontend/api/v1/GeneralLedgerAccount/GeneralLedgerDisplayCriterion.
 *
 * The Persoonscode (-1) and Persoonsnaam (-2) entries are handled transparently by
 * the backend: add/remove them by their number/id like any other criterion.
 */
@Injectable({
  providedIn: 'root',
})
export class GeneralLedgerDisplayCriterionService {
  private apiUrl =
    environment.apiUrl + '/kbj-frontend/api/v1/GeneralLedgerAccount/GeneralLedgerDisplayCriterion';
  private readonly httpOptions = { withCredentials: true };
  private http: HttpClient = inject(HttpClient);

  /**
   * Returns the fixed list of selectable booking descriptions (Dutch and English).
   * Returns empty lists when the API responds with 204 No Content or 404.
   */
  getBookingDescriptionList(): Observable<BookingDescriptionLists> {
    return this.http
      .get<BookingDescriptionListApiModel | null>(
        this.apiUrl + '/BookingDescriptionList',
        this.httpOptions,
      )
      .pipe(
        map((data) => ({
          nl: (data?.nl ?? []).map((item) => this.mapDescription(item)),
          en: (data?.en ?? []).map((item) => this.mapDescription(item)),
        })),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of<BookingDescriptionLists>({ nl: [], en: [] });
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Returns the display criteria currently in use for the given account.
   * Returns an empty array when the API responds with 204 No Content or 404.
   */
  getByAccount(generalLedgerAccountId: string): Observable<DisplayCriterion[]> {
    return this.http
      .get<DisplayCriterionApiModel[] | null>(
        this.apiUrl + '/ByContext/' + generalLedgerAccountId,
        this.httpOptions,
      )
      .pipe(
        map((data) => (data ?? []).map((item) => this.mapToModel(item))),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of<DisplayCriterion[]>([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Adds a criterion for the given account/number. The id is forced to the empty
   * guid so the backend generates a new one.
   */
  add(generalLedgerAccountId: string, number: number): Observable<unknown> {
    const payload: DisplayCriterionApiPayload = {
      generalLedgerDisplayCriterion_ID: EMPTY_GUID,
      generalLedgerAccount_ID: generalLedgerAccountId,
      number,
    };

    return this.http
      .post<unknown>(this.apiUrl, payload, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /**
   * Removes a criterion by its id. Also works for the faked Persoonscode/-naam ids
   * returned by {@link getByAccount}.
   */
  remove(id: string): Observable<unknown> {
    return this.http
      .delete(this.apiUrl + '/' + id, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  private mapDescription(api: BookingDescriptionApiItem): BookingDescriptionItem {
    return { number: api.number, description: api.bookingDescription };
  }

  private mapToModel(api: DisplayCriterionApiModel): DisplayCriterion {
    return {
      generalLedgerDisplayCriterionId: api.generalLedgerDisplayCriterion_ID,
      generalLedgerAccountId: api.generalLedgerAccount_ID,
      number: api.number,
      descriptionNL: api.bookingDescriptionNL ?? '',
      descriptionEN: api.bookingDescriptionEN ?? '',
    };
  }

  private handleError(err: HttpErrorResponse) {
    const errorMessage = `An error has occurred: ${err.error?.message ?? err.message}`;
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }
}
